<template>
  <div class="custom-notification" :class="item.type">
    <div class="custom-notification__icon" :class="item.type">
      <SbIcon :name="computedIcon" color="tertiary" />
    </div>
    <div class="custom-notification__content">
      <div class="custom-notification__title">
        {{ computedTitle }}
      </div>
      <div
        v-if="hasText"
        v-dompurify-html="item.text"
        class="custom-notification__text"
      ></div>
      <SbLink
        v-if="linkData"
        :label="linkData.linkText"
        as="router-link"
        subtle
        :to="sanitizeUrl(linkData.link)"
        class="custom-notification__link"
      />
    </div>
    <SbIconButton
      size="small"
      class="custom-notification__close"
      :label="$t('Close Notification')"
      :tooltip-zindex="toolTipZindex"
      icon="x"
      @click="close"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { sanitizeUrl } from '@braintree/sanitize-url'
import type { NotificationItem } from '@kyvg/vue3-notification'
import { SbIcon, SbIconButton, SbLink } from '@storyblok/design-system'

type LinkData = {
  link: string
  linkText: string
}

const props = defineProps<{
  item: NotificationItem
  close: (payload: MouseEvent) => void
}>()

const computedTitle = computed(() => {
  return !props.item.title ? props.item.text : props.item.title
})

const computedIcon = computed(() => {
  if (props.item.type === 'info') {
    return 'info'
  }

  return props.item.type === 'success' ? 'check' : 'x'
})

const linkData = computed((): LinkData | undefined => {
  const data = props.item.data
  if (
    data &&
    typeof data === 'object' &&
    'link' in data &&
    'linkText' in data &&
    typeof data.link === 'string' &&
    typeof data.linkText === 'string' &&
    data.link !== undefined &&
    data.linkText !== undefined
  ) {
    return {
      link: data.link,
      linkText: data.linkText,
    }
  }
  return undefined
})

const hasText = computed(() => {
  return props.item?.title && props.item?.text
})

const vueNotificationZindex = 10000 // from the scss variable
const toolTipZindex = vueNotificationZindex + 10 // make sure the tooltip is above the notification
</script>
